import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './ReviewList.css';

interface Review {
  id: string;
  customerName: string;
  customerPhone: string;
  rating: number;
  feedback: string;
  staffName?: string;
  serviceName?: string;
  isInternal: boolean;
  createdAt: string;
}

const Stars: React.FC<{ value: number }> = ({ value }) => (
  <span className="rl-stars">
    {[1,2,3,4,5].map(s => (
      <span key={s} className={s <= value ? 'star filled' : 'star'}>★</span>
    ))}
  </span>
);

const ReviewList: React.FC = () => {
  const { language, t } = useLanguage();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toLocaleDateString('en-CA'));
  const [ratingFilter, setRatingFilter] = useState('');

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFilter) params.date = dateFilter;
      if (ratingFilter) params.rating = ratingFilter;
      const res = await apiService.getReviews(params);
      setReviews(res?.data || []);
    } catch (e) {
      console.error('Failed to fetch reviews:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, [dateFilter, ratingFilter]);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '--';

  const label = (r: number) => {
    switch (r) {
      case 1: return t('review.terrible') || 'Rất tệ';
      case 2: return t('review.bad') || 'Tệ';
      case 3: return t('review.average') || 'Bình thường';
      case 4: return t('review.good') || 'Tốt';
      case 5: return t('review.excellent') || 'Tuyệt vời!';
      default: return '';
    }
  };

  return (
    <div className="rl-container">
      <div className="rl-header">
        <div>
          <h1>⭐ {t('review.listTitle')}</h1>
          <p className="rl-sub">{t('review.listSub')}</p>
        </div>
        <div className="rl-stats-box">
          <div className="rl-avg">{avgRating}</div>
          <Stars value={Math.round(Number(avgRating))} />
          <div className="rl-count">{reviews.length} {t('review.reviews')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="rl-filters">
        <label>
          📅
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="rl-input"
          />
        </label>
        <label>
          ⭐
          <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} className="rl-input">
            <option value="">{t('review.allRatings')}</option>
            {[1,2,3,4,5].map(r => <option key={r} value={r}>{r} ★</option>)}
          </select>
        </label>
        <button className="rl-refresh-btn" onClick={fetchReviews}>🔄</button>
      </div>

      {loading ? (
        <div className="rl-loading">
          <div className="rl-spinner" />
          <span>{t('review.loading')}</span>
        </div>
      ) : reviews.length === 0 ? (
        <div className="rl-empty">
          <div style={{ fontSize: '3rem' }}>💭</div>
          <h3>{t('review.noReviews')}</h3>
          <p>{t('review.noReviewsHint')}</p>
        </div>
      ) : (
        <div className="rl-list">
          {reviews.map(review => (
            <div key={review.id} className={`rl-card rating-${review.rating}`}>
              <div className="rl-card-top">
                <div className="rl-card-left">
                  <div className="rl-rating-badge" data-rating={review.rating}>
                    {review.rating}★
                  </div>
                  <div>
                    <div className="rl-customer">{review.customerName}</div>
                    <div className="rl-phone">{review.customerPhone}</div>
                  </div>
                </div>
                <div className="rl-card-right">
                  <Stars value={review.rating} />
                  <div className="rl-label">{label(review.rating)}</div>
                  <div className="rl-time">
                    {new Date(review.createdAt).toLocaleTimeString(language === 'vi' ? 'vi-VN' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
              {review.feedback && (
                <div className="rl-feedback">
                  💬 &ldquo;{review.feedback}&rdquo;
                </div>
              )}
              <div className="rl-card-bottom">
                {review.staffName && (
                  <span className="rl-tag staff">👩‍💼 {review.staffName}</span>
                )}
                {review.serviceName && (
                  <span className="rl-tag service">💇 {review.serviceName}</span>
                )}
                {review.rating === 5 && (
                  <span className="rl-tag google">🗺️ Google</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewList;
